import React, { useEffect, useMemo, useRef } from 'react';

import { parseNordBookingPreselectionUrl } from '../../utils/bookingPreselection';

/* eslint-disable @typescript-eslint/no-namespace */
declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'book-appointment-widget': any;
    }
  }
}

const WIDGET_SCRIPT_SRC =
  'https://pictures.nordhealth.fi/booking-portal-widget.js';
const WIDGET_CSS_HREF =
  'https://pictures.nordhealth.fi/booking-portal-widget.css';

const WIDGET_DOM_ID = 'book-appointment-widget';

const CUSTOM_WIDGET_CSS = `
  /* Hide clinic and city dropdown as they never change. Counted among the
     dropdowns only, so controls the widget adds above them (such as the
     Toimipaikassa / Etänä toggle) do not shift these selectors. */
  .bp-widget-header > nord-dropdown:nth-of-type(1),
  .bp-widget-header > nord-dropdown:nth-of-type(3) {
    display: none !important;
  }

  /* Below desktop the same two filters render as buttons instead of dropdowns. */
  .bp-widget.is-mobile .bp-widget-header > nord-button:nth-of-type(1),
  .bp-widget.is-mobile .bp-widget-header > nord-button:nth-of-type(3),
  .bp-widget.is-tablet .bp-widget-header > nord-button:nth-of-type(1),
  .bp-widget.is-tablet .bp-widget-header > nord-button:nth-of-type(3) {
    display: none !important;
  }

  .bp-widget .bp-widget-header {
    /* Wrapping breaks the filter column into a second column as soon as its
       content is taller than the widget, dropping the calendar on top of the
       results list. */
    flex-wrap: nowrap;
  }

  nord-dropdown {
    width: 300px;
  }

  /* Only the results column is given a height; the filter column stretches to
     match it, so the two cannot drift out of alignment when the widget gains
     new filters. Keep this taller than the filter column needs. */
  .bp-widget:not(.is-mobile):not(.is-tablet) .bp-widget-content {
    height: 720px !important;
  }

  /* Narrow layouts show the filters as an overlay instead of a column, so they
     do not need the extra height. */
  .bp-widget.is-mobile .bp-widget-content,
  .bp-widget.is-tablet .bp-widget-content {
    height: 620px !important;
  }

  .bp-widget-content:has(> .bp-no-results) {
    overflow: hidden;
  }

  :host {
    max-width: 1200px !important;
  }
`;

function syncEnableTimeRangeOnElement(enabled: boolean) {
  const el = document.getElementById(WIDGET_DOM_ID);
  if (!el) return;
  if (enabled) {
    el.setAttribute('enable-time-range', '');
  } else {
    el.removeAttribute('enable-time-range');
  }
}

// TODO: Replace hacky script and custom css injection trickery
const addWidgetAssets = (onScriptLoad: () => void) => {
  const script = Object.assign(document.createElement('script'), {
    src: WIDGET_SCRIPT_SRC,
    type: 'text/javascript',
    async: true,
  });

  const link = Object.assign(document.createElement('link'), {
    rel: 'stylesheet',
    href: WIDGET_CSS_HREF,
    type: 'text/css',
  });

  document.body.appendChild(script);
  document.head.appendChild(link);

  let cleanupShadowWait: (() => void) | undefined;

  script.onload = () => {
    const widget = document.getElementById(WIDGET_DOM_ID);
    const setupShadow = (shadowRoot: ShadowRoot) => {
      const style = document.createElement('style');
      style.textContent = CUSTOM_WIDGET_CSS;
      shadowRoot.appendChild(style);
    };
    if (widget) {
      if (widget.shadowRoot) {
        setupShadow(widget.shadowRoot);
      } else {
        cleanupShadowWait = waitForShadowRoot(widget, setupShadow);
      }
    }
    onScriptLoad();
  };

  return () => {
    cleanupShadowWait?.();
    document.body.removeChild(script);
    document.head.removeChild(link);
  };
};

/** Shadow root attachment does not mutate light-DOM children; poll until it exists. */
function waitForShadowRoot(
  widget: HTMLElement,
  cb: (shadowRoot: ShadowRoot) => void,
  maxMs = 15000
): () => void {
  let cancelled = false;
  let raf = 0;
  const start = Date.now();
  const tick = () => {
    if (cancelled) return;
    if (widget.shadowRoot) {
      cb(widget.shadowRoot);
      return;
    }
    if (Date.now() - start > maxMs) return;
    raf = requestAnimationFrame(tick);
  };
  tick();
  return () => {
    cancelled = true;
    cancelAnimationFrame(raf);
  };
}

const BookingPortal = ({
  preselectionUrl,
  enableTimeRangeFilter,
}: {
  preselectionUrl?: string;
  /** When true, sets boolean attribute enable-time-range (no value) on the custom element. */
  enableTimeRangeFilter?: boolean;
}) => {
  const parsed = useMemo(
    () => parseNordBookingPreselectionUrl(preselectionUrl),
    [preselectionUrl]
  );

  const enableTimeRangeRef = useRef(!!enableTimeRangeFilter);
  enableTimeRangeRef.current = !!enableTimeRangeFilter;

  useEffect(() => {
    const cleanup = addWidgetAssets(() => {
      syncEnableTimeRangeOnElement(enableTimeRangeRef.current);
    });
    return cleanup;
  }, []);

  useEffect(() => {
    syncEnableTimeRangeOnElement(!!enableTimeRangeFilter);
  }, [enableTimeRangeFilter]);

  return (
    <book-appointment-widget
      language="fi"
      company-id="6d1e6cd7-6229-4846-8967-a04d5d7a0914"
      id={WIDGET_DOM_ID}
      preselected-service={parsed.preselectedServiceId}
      preselected-therapist={parsed.preselectedTherapistUuid}
      enable-time-range
    />
  );
};

export default BookingPortal;
